<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import useLaboratory from "@/laboratory/composables/useLaboratory";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import LaboratoryHomeStatisticCard from "@/laboratory/components/home/LaboratoryHomeStatisticCard.vue";
import {currencyze} from "@/core/filters/Currencyze";
import LaboratoryExtensions from "@/laboratory/components/LaboratoryExtensions.vue";
import useLaboratoryStatistics from "@/laboratory/composables/useLaboratoryStatistics";
import LaboratoryHomePatientCard from "@/laboratory/components/home/LaboratoryHomePatientCard.vue";
import {Poll, ProgramType} from "@/program/interfaces/Program";
import useLaboratoryColor from "@/laboratory/composables/useLaboratoryColor";
import LaboratoryKnowledgeableButton from "@/laboratory/components/LaboratoryKnowledgeableButton.vue";
import LaboratoryHomeAnimationCard from "@/laboratory/components/home/LaboratoryHomeAnimationCard.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import LaboratoryDashboard from "@/laboratory/components/LaboratoryDashboard.vue";
import {pollApi} from "@/container";
import LaboratorySharingView from "@/laboratory/components/LaboratorySharingView.vue";
import {useSystemStore} from "@/stores/system.store";
import LaboratoryAttachmentPreview from "@/laboratory/components/LaboratoryAttachmentPreview.vue";
import PollToggle from "@/program/components/polls/PollToggle.vue";
import LaboratoryHomeCatalogCard from "@/laboratory/components/home/LaboratoryHomeCatalogCard.vue";
import {useCartStore} from "@/laboratory/stores/cart.store";
import {storeToRefs} from "pinia";

const props = defineProps<{
    laboratoryId: string;
}>();

const {data: laboratory} = useLaboratory(props.laboratoryId);
const {primaryColor, secondaryColor} = useLaboratoryColor({
    primary: laboratory.value?.branding?.primary_color,
    secondary: laboratory.value?.branding?.secondary_color
});
const {data: statistics} = useLaboratoryStatistics(props.laboratoryId);
const polls = ref<Poll[]>([]);
const {environment} = useSystemStore();
const {franco} = storeToRefs(useCartStore());

/**
 * Whether ordering is available, i.e. the healthcenter has a franco with the laboratory.
 * The layout fetches it and gates the cart button on the very same value.
 */
const orderable = computed(() => !!franco.value);

onMounted(() => {
    pollApi().index({laboratory_id: Number(props.laboratoryId)})
        .then(response => {
            polls.value = response;
        });
});


/**
 * Get all the extensions other than loyalty cards.
 */
const extensionsOtherThanLoyaltyCards = computed(() => {
    if (!laboratory.value?.extensions) {
        return [];
    }

    return laboratory.value.extensions.filter((extension) => {
        if (!('sub_type' in extension)) {
            return true;
        }

        return extension.sub_type !== ProgramType.LOYALTY_CARD;
    });
});
</script>

<template>
  <BaseFlexSpinner v-if="!laboratory" />
  <BaseContainer v-else class="p-6 flex flex-col gap-6">
    <div class="flex flex-col sm:flex-row justify-between flex-wrap gap-6">
      <div class="flex gap-6">
        <LaboratoryHomeStatisticCard class="flex-1" :color="primaryColor">
          <div class="flex gap-2">
            <label class="font-content-bold">
              {{ statistics?.consumer_count ?? 0 }}
            </label> {{ $filters.pluralize("Client", statistics?.consumer_count ?? 0) }}
          </div>
          <template #tooltip>
            Nombre de patients ayant acheté un produit {{ laboratory.name }} au cours des 12 derniers mois.
          </template>
        </LaboratoryHomeStatisticCard>
        <LaboratoryHomeStatisticCard class="flex-1" :color="primaryColor">
          <div class="flex gap-2">
            <label class="font-content-bold">
              {{ currencyze(statistics?.total_revenue ?? 0) }}
            </label> CA
          </div>
          <template #tooltip>
            Chiffre d'affaires total généré par les ventes de produits {{ laboratory?.name }} au cours des 12 derniers
            mois.
          </template>
        </LaboratoryHomeStatisticCard>
      </div>
      <LaboratoryKnowledgeableButton
        v-if="laboratory?.knowledgeables?.length"
        :knowledgeable="laboratory.knowledgeables[0]"
        :color="primaryColor"
      />
    </div>
    <div class="flex flex-col flex-wrap lg:flex-row gap-x-16 gap-y-6">
      <PollToggle
        v-if="polls && polls.length > 0"
        :polls="polls"
        class="mx-6"
      />
      <LaboratorySharingView
        v-if="environment?.entityId"
        class="flex-1"
        :laboratory-id="laboratory.id"
        :healthcenter-id="environment.entityId"
      />
      <LaboratoryHomeAnimationCard
        class="flex-1"
        :laboratory-name="laboratory.name"
        :today-consumers="statistics?.today_consumer_count"
        :primary-color="primaryColor"
        :secondary-color="secondaryColor"
        :laboratory-id="laboratoryId"
      />
      <LaboratoryExtensions
        v-if="extensionsOtherThanLoyaltyCards.length"
        class="flex-1"
        :extensions="extensionsOtherThanLoyaltyCards"
      />
      <LaboratoryHomeCatalogCard
        v-if="orderable"
        class="flex-1"
        :laboratory-name="laboratory.name"
        :primary-color="primaryColor"
        :secondary-color="secondaryColor"
        :laboratory-id="laboratoryId"
      />
    </div>
    <LaboratoryHomePatientCard v-if="laboratory.has_patient_space" :laboratory-id="laboratoryId" />
    <LaboratoryDashboard :laboratory-id="laboratoryId" />
    <LaboratoryAttachmentPreview :laboratory-id="laboratoryId" />
  </BaseContainer>
</template>

<style scoped>

</style>
